import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isDemoMode: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  enterDemoMode: () => void;
  exitDemoMode: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isDemoMode: false,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
  enterDemoMode: () => {},
  exitDemoMode: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const { toast } = useToast();

  // Create demo user object
  const createDemoUser = (): User => ({
    id: 'demo-user-id',
    aud: 'authenticated',
    role: 'authenticated',
    email: 'demo@ztart.com',
    email_confirmed_at: new Date().toISOString(),
    phone: '',
    confirmed_at: new Date().toISOString(),
    last_sign_in_at: new Date().toISOString(),
    app_metadata: {},
    user_metadata: {
      full_name: 'Demo User',
      avatar_url: '',
      ztartag: 'demouser'
    },
    identities: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });

  useEffect(() => {
    console.log('Setting up auth state change listener');
    
    // Check for demo mode in localStorage
    const savedDemoMode = localStorage.getItem('ztart_demo_mode');
    if (savedDemoMode === 'true') {
      setIsDemoMode(true);
      setUser(createDemoUser());
      setLoading(false);
      return;
    }
    
    // Get initial session with better error handling
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
          // Clear any potentially corrupted session data
          await supabase.auth.signOut();
        } else {
          console.log('Initial session:', session);
          setUser(session?.user ?? null);
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error);
        // Clear any potentially corrupted session data
        await supabase.auth.signOut();
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes with improved error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session);
        
        // Skip auth events if in demo mode
        if (isDemoMode) return;
        
        // Handle different auth events
        if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          setUser(session?.user ?? null);
        } else if (event === 'SIGNED_IN') {
          setUser(session?.user ?? null);
          toast({
            title: 'Login successful',
            description: 'Welcome to Ztart',
          });
        } else if (event === 'USER_UPDATED') {
          setUser(session?.user ?? null);
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [toast, isDemoMode]);

  const enterDemoMode = () => {
    setIsDemoMode(true);
    setUser(createDemoUser());
    setLoading(false);
    localStorage.setItem('ztart_demo_mode', 'true');
    
    toast({
      title: 'Demo Mode',
      description: 'Demo mode is now active',
    });
  };

  const exitDemoMode = () => {
    setIsDemoMode(false);
    setUser(null);
    localStorage.removeItem('ztart_demo_mode');
    
    toast({
      title: 'Exit Demo',
      description: 'Demo mode deactivated',
    });
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('Sign in error:', error);
        toast({
          title: 'Login Error',
          description: error.message,
          variant: "destructive",
        });
      }
      
      return { error };
    } catch (error) {
      console.error('Unexpected sign in error:', error);
      toast({
        title: 'Login Error',
        description: 'An unexpected error occurred',
        variant: "destructive",
      });
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      
      if (error) {
        console.error('Sign up error:', error);
        toast({
          title: 'Registration Error',
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: 'Account Created',
          description: 'Please check your email to verify your account',
        });
      }
      
      return { error };
    } catch (error) {
      console.error('Unexpected sign up error:', error);
      toast({
        title: 'Registration Error',
        description: 'An unexpected error occurred',
        variant: "destructive",
      });
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      
      // If in demo mode, just exit demo mode
      if (isDemoMode) {
        exitDemoMode();
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        toast({
          title: 'Sign Out Failed',
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: 'Signed Out',
          description: 'You have been successfully signed out',
        });
      }
    } catch (error) {
      console.error('Unexpected sign out error:', error);
      toast({
        title: 'Sign Out Failed',
        description: 'Failed to sign out properly',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    isDemoMode,
    signIn,
    signUp,
    signOut,
    enterDemoMode,
    exitDemoMode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
